import {
  AiPanel,
  Alert,
  ControlLabel,
  DatePicker,
  Dialog,
  FormSection,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@all-manager/design-system-v2';
import { startOfDay } from 'date-fns';
import { ChangeEvent } from 'react';
import { Controller } from 'react-hook-form';
import DescriptionFieldOk from '../../../../components/modals/components/DescriptionFieldOk';
import { DocumentMinimalData, ProviderContractDatabase } from '../../../../types';
import DriveFileField from '../../../documents/external-components/DriveFileField/DriveFileField';
import ProviderAutocomplete from '../../external-components/ProviderAutocomplete/ProviderAutocomplete';
import ContractAiAnalyzing from '../ContractAiAnalyzing/ContractAiAnalyzing';
import ContractAiDropzone from '../ContractAiDropzone/ContractAiDropzone';
import useAddUpdateContractForm, { type InitialContractFormValues } from './useAddUpdateContractForm';

function toDay(date: Date | null) {
  return date ? startOfDay(date) : null;
}

type Props = {
  onClose: () => void;
  providerId: string;
  contract?: ProviderContractDatabase;
  initialValues?: InitialContractFormValues | null;
  initialDocument?: DocumentMinimalData | null;
  isAiPreFilled?: boolean;
  showProviderSelect?: boolean;
  onSuccess?: () => void;
};

export default function AddUpdateContractModal(props: Props) {
  const hook = useAddUpdateContractForm({
    providerId: props.providerId,
    contract: props.contract,
    initialValues: props.initialValues,
    initialDocument: props.initialDocument,
    isAiPreFilled: props.isAiPreFilled,
    onClose: props.onClose,
    onSuccess: props.onSuccess,
  });

  const hasAutomaticRenewal = hook.form.watch('has_automatic_renewal');

  function handleRenewalChange(event: ChangeEvent<HTMLInputElement>) {
    hook.form.setValue('has_automatic_renewal', event.target.value === 'true');
  }

  // Step 1: Dropzone for AI contract analysis (when AI is enabled and creating new contract)
  if (hook.step === 'dropzone') {
    return (
      <Dialog
        open
        onClose={props.onClose}
        maxWidth="sm"
        fullWidth
        title="Nouveau contrat"
        closeLabel="Fermer"
      >
        <ContractAiDropzone
          onFileSelect={hook.handleStartAnalysis}
          onSkipToManual={hook.handleSkipToManual}
        />
      </Dialog>
    );
  }

  // Step 2: Animated loading state during AI contract parsing
  if (hook.step === 'analyzing') {
    return (
      <Dialog
        open
        onClose={hook.handleCancelAnalysis}
        maxWidth="sm"
        fullWidth
        title="Analyse du contrat"
        closeLabel="Fermer"
      >
        <ContractAiAnalyzing
          fileName={hook.analyzingFileName}
          onCancel={hook.handleCancelAnalysis}
        />
      </Dialog>
    );
  }

  // Step 3: Standard form with pre-filled AI values or manual input
  return (
    <Dialog
      open
      onClose={props.onClose}
      onSubmit={hook.onSubmit}
      maxWidth="md"
      fullWidth
      title={props.contract ? 'Modification du contrat' : 'Nouveau contrat'}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: props.contract ? 'Modifier' : 'Créer',
          type: 'submit',
          loading: hook.isSubmitting,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: hook.isSubmitting,
        },
      }}
    >
      <Stack spacing={3}>
        {hook.serverError ? <Alert severity="error">{hook.serverError}</Alert> : null}
        {hook.analysisWarning ? <Alert severity="warning">{hook.analysisWarning}</Alert> : null}

        {hook.isAiPreFilled ? (
          <AiPanel title="Contrat analysé par l'IA" badgeLabel="Pré-remplissage IA">
            <Typography variant="body2">
              Les dates, la reconduction, le préavis et les missions ont été extraits de votre contrat. Vous pouvez vérifier et ajuster chaque champ avant d'enregistrer.
            </Typography>
          </AiPanel>
        ) : null}

        {(props.showProviderSelect || !props.providerId) ? (
          <Controller
            control={hook.form.control}
            name="provider_id"
            render={({ field, fieldState }) => (
              <ProviderAutocomplete
                value={field.value || null}
                onChange={(val) => field.onChange(val || '')}
                label="Prestataire"
                required
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        ) : null}

        <Controller
          control={hook.form.control}
          name="name"
          render={({ field, fieldState }) => (
            <TextField
              name={field.name}
              inputRef={field.ref}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value ?? ''}
              label="Nom"
              fullWidth
              required
              autoFocus={!hook.isAiPreFilled}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <FormSection title="Engagement">
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={hook.form.control}
                name="start_at"
                render={({ field, fieldState }) => (
                  <DatePicker
                    label="Date de souscription"
                    value={field.value}
                    onChange={(date) => field.onChange(toDay(date))}
                    inputRef={field.ref}
                    required
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={hook.form.control}
                name="price"
                render={({ field }) => (
                  <TextField
                    name={field.name}
                    inputRef={field.ref}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value ?? ''}
                    label="Prix"
                    placeholder="Ex. : 1 200 € HT / an"
                    fullWidth
                    multiline
                    minRows={1}
                  />
                )}
              />
            </Grid>
          </Grid>

          <RadioGroup
            row
            label="Contrat à tacite reconduction"
            name="has_automatic_renewal"
            value={String(hasAutomaticRenewal)}
            onChange={handleRenewalChange}
          >
            <ControlLabel value="true" control={<Radio />} label="Oui" />
            <ControlLabel value="false" control={<Radio />} label="Non" />
          </RadioGroup>

          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {hasAutomaticRenewal ? (
                <Controller
                  control={hook.form.control}
                  name="duration_of_renewal"
                  render={({ field, fieldState }) => (
                    <TextField
                      name={field.name}
                      inputRef={field.ref}
                      onBlur={field.onBlur}
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value === '' ? null : Number(event.target.value))}
                      label="Durée de la reconduction (en années)"
                      type="number"
                      fullWidth
                      slotProps={{ htmlInput: { min: 1, inputMode: 'numeric' } }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              ) : (
                <Controller
                  control={hook.form.control}
                  name="end_at"
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label="Date d'échéance"
                      value={field.value}
                      onChange={(date) => field.onChange(toDay(date))}
                      inputRef={field.ref}
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={hook.form.control}
                name="period_of_notice"
                render={({ field }) => (
                  <TextField
                    name={field.name}
                    inputRef={field.ref}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value ?? ''}
                    label="Durée du préavis"
                    placeholder="Ex. : 3 mois"
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Détails">
          <DescriptionFieldOk
            form={hook.form}
            name="description"
            label="Informations clefs"
            isAiGenerated={hook.isAiPreFilled}
          />

          <DriveFileField
            label="Le contrat"
            value={hook.document}
            onChange={hook.setDocument}
          />
        </FormSection>
      </Stack>
    </Dialog>
  );
}
